import { showToast } from '@/helper/toast.helper';

import { usePermission } from '@/hooks/usePermission';
import { AppDispatch, RootState } from '@/store';
import { getProjectById, updateProjects } from '@/store/slices/projectsSlice/projectsSlice';
import { toggleSiteStatus } from '@/store/slices/siteManagementSlice/siteManagementSlice';
import { Project, Site, UpdateProjectDto } from '@/types/project.types';
import { SiteByProjectIdDto, SiteInfo, SiteListItemDto } from '@/types/site.types';
import L from 'leaflet';
import {
  ArrowDown,
  ArrowUp,
  BellRing,
  Clipboard,
  Eye,
  Plus,
  SendHorizontal,
  UserPlus,
  Users,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Circle, MapContainer, Marker, Popup, TileLayer, Tooltip, useMap } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import AddUserToProjectModal from '../CommonComponents/AddUserToProjectModal';
import ClientSearchFilter, { ClientFilterField } from '../CommonComponents/ClientSearchFilter';
import MapSiteTile from '../CommonComponents/MapSiteTile';
import ProjectInfoCard from '../CommonComponents/ProjectInfoCard';
import SiteInfoCard from '../CommonComponents/SiteInfoCard';
import StatCard from '../CommonComponents/StatCard';
import ViewDetailsModal from '../CommonComponents/ViewDetailsModal';
import PageHeaderWrapper from '../Layout/PageHeaderWrapper';

interface LocationState {
  project: Project;
}

const redUserIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const ProjectView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;

  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [filteredSites, setFilteredSites] = useState<Site[]>([]);
  const [filterBy, setFilterBy] = useState<'Sites' | 'Users' | null>(null);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [tileOpen, setTileOpen] = useState<boolean>(false);
  const [tilePosition, setTilePosition] = useState<{ x: number; y: number } | null>(null);
  const [openSiteModal, setOpenSiteModal] = useState(false);
  const [expandSites, setExpandSites] = useState(false);
  const [currentProject, setCurrentProject] = useState<Project | null>(
    location.state?.project || null,
  );

  const dispatch = useDispatch<AppDispatch>();

  const { selectedProjectStats, loading, updatingProject } = useSelector(
    (state: RootState) => state.projects,
  );
  const project = currentProject;

  const SelectedSiteTracker: React.FC<{ selectedSite: Site | null; setTilePosition: any }> = ({
    selectedSite,
    setTilePosition,
  }) => {
    const map = useMap();

    useEffect(() => {
      if (!map || !selectedSite) return;

      const updatePosition = () => {
        const point = map.latLngToContainerPoint([
          selectedSite.geofence.centerLat!,
          selectedSite.geofence.centerLng!,
        ]);
        setTilePosition({ x: point.x, y: point.y });
      };

      map.on('move zoom', updatePosition);

      return () => {
        map.off('move zoom', updatePosition);
      };
    }, [map, selectedSite]);

    return null;
  };

  const permissions = {
    edit: usePermission('write', 'projects', { projectId: location?.state?.project?.id }),
    export: usePermission('export', 'projects', { projectId: location?.state?.project?.id }),
    create: usePermission('write', 'surveySets', { projectId: location?.state?.project?.id }),
    createUser: usePermission('write', 'project_users', {
      projectId: location?.state?.project?.id,
    }),
    createNotification: usePermission('write', 'notifications', {
      projectId: location?.state?.project?.id,
    }),
  };

  const visibleSites = expandSites ? filteredSites : filteredSites.slice(0, 3);

  useEffect(() => {
    if (locationState?.project) {
      setCurrentProject(locationState.project);
      setFilteredSites(locationState.project.sites || []);
      setIsExpanded(false);
      setSelectedSite(null);
    }
  }, [locationState?.project?.id]);

  useEffect(() => {
    if (!project) {
      navigate('/dashboard/project-management');
    } else {
      setFilteredSites(project.sites || []);
    }
  }, [project, navigate]);

  useEffect(() => {
    if (project && !selectedProjectStats) {
      dispatch(getProjectById(project.id.toString()));
    }
  }, [project, selectedProjectStats, dispatch]);

  const filterFields: ClientFilterField[] = useMemo(() => {
    const fields: ClientFilterField[] = [
      {
        type: 'search',
        key: 'search',
        placeholder: 'Search sites by name or country...',
      },
    ];
    if (isExpanded) {
      fields.push({
        type: 'radio-select',
        key: 'filterBy',
        label: 'filter',
        options: ['Sites', 'Users'],
      });
    }

    return fields;
  }, [isExpanded]);

  const customFilterFn = (site: Site, filters: Record<string, any>): boolean => {
    if (filters.search && filters.search.trim() !== '') {
      const searchTerm = filters.search.toLowerCase();
      const matchesName = site.name.toLowerCase().includes(searchTerm);
      const matchesCountry = site.country?.name?.toLowerCase().includes(searchTerm);
      if (!matchesName && !matchesCountry) return false;
    }
    if (filters.filterBy === 'Sites') {
      setFilterBy('Sites');
    } else if (filters.filterBy === 'Users') {
      setFilterBy('Users');
    } else setFilterBy(null);
    return true;
  };

  const handleViewSite = (site: any) => {
    setSelectedSite(site);
    setOpenSiteModal(true);
  };

  const handleNaviagateToSurveySets = () => {};

  const handleToggleStatus = async (site: any) => {
    try {
      const newStatus = !site.status;
      await dispatch(
        toggleSiteStatus({
          id: site.id,
          status: newStatus,
        }),
      ).unwrap();

      setCurrentProject((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sites: prev.sites.map((s) => (s.id === site.id ? { ...s, status: newStatus } : s)),
        };
      });

      if (!newStatus) {
        showToast.success('Site archived successfully.');
      } else {
        showToast.warning('Site unarchived successfully.');
      }
    } catch (error: any) {
      showToast.error(error?.message || 'Failed to update site status');
    }
  };

  const handleGetAllSites = () => {
    navigate('/dashboard/site-management/all-sites', {
      state: { sitesList: filteredSites, from: 'project-management' },
    });
  };

  const handleSiteArrowClick = (site: Site | SiteListItemDto | SiteByProjectIdDto | SiteInfo) => {
    navigate('/dashboard/site-management/EditSite', {
      state: {
        project: project
          ? { id: project?.id, name: project?.name, emoji: project?.emoji, status: project?.status }
          : null,
        siteId: site.id,
        from: 'project-management',
      },
    });
  };

  const getSiteDetailFields = (site: any) => {
    if (!site) return [];

    return [
      { label: 'Site Name', value: site.name || 'N/A', half: true },

      { label: 'Country', value: site.country?.name || 'N/A', half: true },

      {
        label: 'Longitude',
        value: site.geofence?.centerLng !== undefined ? site.geofence.centerLng : 'N/A',
        half: true,
      },

      {
        label: 'Latitude',
        value: site.geofence?.centerLat !== undefined ? site.geofence.centerLat : 'N/A',
        half: true,
      },

      {
        label: 'Radius',
        value:
          site.geofence?.radiusMeters !== undefined ? `${site.geofence.radiusMeters}  m` : 'N/A',
        half: true,
      },

      { label: 'Max Users', value: site.maxUsers ?? 'N/A', half: true },

      {
        label: 'Language(s)',
        value:
          Array.isArray(site.languages) && site.languages.length > 0
            ? site.languages.map((l: any) => l.name).join(', ')
            : 'N/A',
        colSpan: 2,
      },

      { label: 'Reward Currency', value: site.currency || 'N/A', half: true },

      { label: 'Project', value: project?.name || 'N/A', half: true },

      {
        label: 'Status',
        value: typeof site.status === 'boolean' ? (site.status ? 'Active' : 'Inactive') : 'N/A',
        badge: true,
        colSpan: 2,
      },
    ];
  };

  const handleFilterChange = useCallback((filtered: Site[]) => {
    setFilteredSites(filtered);
  }, []);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  const handleEditSite = (site: Site) => {
    navigate('/dashboard/site-management/EditSite', {
      state: { siteId: site.id, from: 'project-management', project: project },
    });
  };

  const handleArchiveProjectClick = async () => {
    if (!project) return;

    try {
      const updateData: UpdateProjectDto = {
        status: !project.status,
      };

      await dispatch(updateProjects({ id: project.id.toString(), data: updateData })).unwrap();

      setCurrentProject({
        ...project,
        status: !project.status,
      });
      showToast.success(`Project ${!!project.status ? 'Archived' : 'Unarchived'} Successfully!`);
    } catch (error) {
      showToast.error((error as string) || 'Error updating the Project status');
    }
  };

  const ResizeMap = ({ isExpanded }: { isExpanded: boolean }) => {
    const map = useMap();
    useEffect(() => {
      if (map) {
        setTimeout(() => {
          try {
            map.invalidateSize();
          } catch (error) {
            console.error('Map invalidation error:', error);
          }
        }, 300);
      }
    }, [isExpanded, map]);
    return null;
  };

  const projectStats = [
    {
      id: 'totalUsers',
      title: 'Total System Users',
      value: selectedProjectStats?.totalusers || 0,
      icon: Users,
      iconBg: 'bg-blue-200',
    },
    {
      id: 'totalSurveySets',
      title: 'Total Survey Sets',
      value: selectedProjectStats?.totalSurveySets || 0,
      icon: Clipboard,
      iconBg: 'bg-[#EDAAC5]',
    },
    {
      id: 'activeSurveys',
      title: 'Active Surveys',
      value: 0,
      icon: Clipboard,
      iconBg: 'bg-[#EDAAC5]',
    },
    {
      id: 'notifications',
      title: 'Notifications',
      value: selectedProjectStats?.notification || 0,
      icon: BellRing,
      iconBg: 'bg-[#F3C68D]',
    },
  ];

  const totalMaxUsers = project?.sites?.reduce((sum, site) => sum + (site?.maxUsers || 0), 0);
  const sitesData = useMemo(() => project?.sites || [], [project?.sites]);

  return (
    <PageHeaderWrapper
      title={'Project Management'}
      description={'Manage all projects you have access to'}
      onAdd={() => navigate(-1)}
      addLabel="Return to projects list"
      showExport={false}
      permissions={permissions}
    >
      {/* Project Info Card */}
      <ProjectInfoCard
        project={project!}
        isExpanded={isExpanded}
        showEdit={!isExpanded}
        showFile={!isExpanded}
        enableFile={!updatingProject}
        onEdit={() =>
          navigate('/dashboard/project-management/edit', {
            state: {
              project: {
                id: project?.id,
                name: project?.name,
                maxUsers: totalMaxUsers,
                maxSites: project?.sites?.length,
                sites: project?.sites || [],
                primaryColor: project?.primaryColour,
                secondaryColor: project?.secondaryColour,
                emoji: project?.emoji,
                description: project?.descriptions,
                admins: project?.managers || [],
                users: project?.users,
              },
              from: 'project-management',
            },
          })
        }
        onFile={handleArchiveProjectClick}
        onExpand={toggleExpand}
        showCollapse={isExpanded}
        showExpand={!isExpanded}
        onCollapse={toggleExpand}
        permissions={permissions}
      />

      {/* Search & Filters */}
      <ClientSearchFilter
        fields={filterFields}
        data={sitesData}
        onFilter={handleFilterChange}
        customFilterFn={customFilterFn}
        searchableKeys={['name', 'country']}
      />

      {/* Summary Cards */}
      {!isExpanded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {projectStats.map((item) => (
            <StatCard
              key={item.id}
              title={item.title}
              value={item.value}
              icon={item.icon}
              iconBg={item.iconBg}
              iconSize={40}
              iconStroke={0.8}
            />
          ))}
        </div>
      )}

      {/* Map & Quick Actions */}
      <div
        className={`flex flex-col lg:flex-row gap-4 transition-all duration-500 ${
          isExpanded ? 'items-start' : ''
        }`}
      >
        {/* Site Map */}
        <div
          className={`${
            isExpanded ? 'w-full' : 'lg:w-2/4'
          } bg-[#F2EBEE] rounded-lg p-3 relative transition-all duration-500`}
        >
          <div className="flex justify-between items-center mb-3">
            <h2 className="font-bold text-xl">Site Map</h2>
          </div>

          <div
            className={`relative ${
              isExpanded ? 'h-[70vh]' : 'h-64'
            } w-full rounded-3xl shadow-lg overflow-hidden transition-all duration-500`}
          >
            <MapContainer
              maxBounds={[
                [-90, -180],
                [90, 180],
              ]}
              maxBoundsViscosity={1.0}
              center={[20.5937, 78.9629]}
              zoom={4}
              className="h-full w-full z-0"
            >
              <TileLayer noWrap={true} url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <ResizeMap isExpanded={isExpanded} />
              <SelectedSiteTracker selectedSite={selectedSite} setTilePosition={setTilePosition} />
              {filterBy !== 'Users' &&
                filteredSites.map((site, idx) => {
                  if (site.geofence.centerLat && site.geofence.centerLng)
                    return (
                      <React.Fragment key={idx}>
                        {site.geofence.radiusMeters && (
                          <Circle
                            center={[site.geofence.centerLat, site.geofence.centerLng]}
                            radius={site.geofence.radiusMeters}
                            pathOptions={{
                              fillColor: '#621C43',
                              fillOpacity: 0.2,
                              color: '#621C43',
                              weight: 2,
                            }}
                          />
                        )}
                        {filteredSites.map((site, idx) => (
                          <Marker
                            key={idx}
                            position={[site.geofence.centerLat!, site.geofence.centerLng!]}
                            eventHandlers={{
                              click: (e) => {
                                setSelectedSite(site);
                                setTileOpen(!tileOpen);
                                const map = e.target._map; // get Leaflet map instance
                                if (map) {
                                  const point = map.latLngToContainerPoint(e.latlng);
                                  setTilePosition({ x: point.x, y: point.y });
                                }
                              },
                            }}
                          >
                            <Tooltip>{site.name}</Tooltip>
                          </Marker>
                        ))}
                      </React.Fragment>
                    );
                  return null;
                })}

              {/* {selected site tile on map} */}
              {selectedSite && tilePosition && (
                <MapSiteTile
                  site={selectedSite}
                  position={tilePosition}
                  onView={(site) => {
                    const str: string =
                      'Latitide - ' +
                      site.geofence.centerLat +
                      ' \n ' +
                      'Longitude - ' +
                      site.geofence.centerLng;
                    showToast.info(str);
                  }}
                  onEdit={(site) => handleEditSite(site)}
                  onClose={() => setSelectedSite(null)}
                />
              )}

              {/* USER MARKERS */}
              {filterBy !== 'Sites' &&
                project?.users?.map((user) => {
                  const lat = Number(user.latitude);
                  const lng = Number(user.longitude);

                  if (isNaN(lat) || isNaN(lng)) return null;

                  return (
                    <Marker key={`user-${user.id}`} position={[lat, lng]} icon={redUserIcon}>
                      <Popup>
                        <div className="text-sm">
                          <p className="font-semibold">User ID: {user.userId}</p>
                          <p>Status: {user.status ? 'Active' : 'Inactive'}</p>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
            </MapContainer>

            {/* Expand/Collapse Button */}
            <button
              onClick={toggleExpand}
              className="absolute bottom-3 right-3 p-3 rounded-md text-gray-600 transition z-[10]"
            >
              <img
                src="/expand.png"
                alt={isExpanded ? 'Shrink Map' : 'Expand Map'}
                className={`w-10 h-10 transform transition-transform duration-300 ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        {!isExpanded && (
          <div className="lg:w-2/4 bg-[#F2EBEE] rounded-lg p-3 transition-all duration-300 flex flex-col">
            <h2 className="font-bold text-xl mb-3">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 flex-1 auto-rows-fr gap-x-2">
              <button
                disabled={!permissions?.create}
                onClick={() =>
                  navigate('/dashboard/surveyset-management/create', {
                    state: { fromProject: { id: project?.id, name: project?.name } },
                  })
                }
                className="bg-[#EDAAC5] rounded-xl flex flex-col items-center gap-1 justify-center w-full disabled:cursor-not-allowed"
              >
                <Plus size={35} color="#000" strokeWidth={1} />
                <p className="text-base text-center px-4">Create Survey(s) Set</p>
              </button>
              <button
                disabled={!permissions.createUser}
                className="bg-blue-200 rounded-xl flex flex-col items-center gap-1 justify-center w-full disabled:cursor-not-allowed"
                onClick={() => setIsAddUserOpen(true)}
              >
                <UserPlus size={35} color="#000" strokeWidth={1} />
                <p className="text-base text-center px-4">Add Project Participant(s)</p>
              </button>
              <button
                disabled={!permissions.createNotification}
                onClick={() =>
                  navigate('/dashboard/notification-management/create', {
                    state: { fromProject: { id: project?.id, name: project?.name } },
                  })
                }
                className="bg-[#F3C68D] rounded-xl flex flex-col items-center gap-1 justify-center w-full disabled:cursor-not-allowed"
              >
                <SendHorizontal size={35} color="#000" strokeWidth={1} />
                <p className="text-base text-center px-4">Send Notification(s)</p>
              </button>
              <button
                onClick={() =>
                  navigate('/dashboard/surveyset-management', {
                    state: { projectId: project?.id },
                  })
                }
                className="bg-[#F0D1DD] rounded-xl flex flex-col items-center gap-1 justify-center w-full"
              >
                <Eye size={35} color="#000" strokeWidth={1} />
                <p className="text-base text-center px-4">View Survey(s) Set</p>
              </button>
              <button
                onClick={() =>
                  navigate('/dashboard/project-management/user-management', {
                    state: {
                      projectId: project?.id,
                    },
                  })
                }
                className="bg-[#D1D7EC] rounded-xl flex flex-col items-center gap-1 justify-center w-full"
              >
                <Eye size={35} color="#000" strokeWidth={1} />
                <p className="text-base text-center px-4">View Project Participant(s)</p>
              </button>
              <button
                onClick={() =>
                  navigate('/dashboard/notification-management', {
                    state: { projectId: project?.id },
                  })
                }
                className="bg-[#F3DCC7] rounded-xl flex flex-col items-center gap-1 justify-center w-full"
              >
                <Eye size={35} color="#000" strokeWidth={1} />
                <span className="mt-2 text-sm text-center">View Notifications</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <ViewDetailsModal
        isOpen={openSiteModal}
        onClose={() => setOpenSiteModal(false)}
        title="Site Details"
        data={selectedSite || {}}
        fields={getSiteDetailFields(selectedSite)}
      />

      {/* Site Cards */}
      <div className="">
        {/* Grid of Sites */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {visibleSites.map((site) => (
            <SiteInfoCard
              key={site.id}
              site={site}
              onView={handleViewSite}
              handleArrowClick={handleSiteArrowClick}
              onToggleStatus={handleToggleStatus}
              permissions={permissions}
            />
          ))}

          {/* See More Card */}
          {!expandSites && filteredSites.length > 4 && (
            <button
              onClick={() => setExpandSites(true)}
              className="relative flex flex-col justify-between bg-[#AFAFAF] text-[#2B1C25] rounded-lg shadow hover:bg-[#9e8895] transition-all p-5 w-full"
            >
              <div className="flex flex-col items-start">
                <span className="text-2xl font-bold">See More</span>
                <span className="text-sm">
                  {filteredSites.length - 3} site
                  {filteredSites.length !== 1 ? 's' : ''} remaining
                </span>
              </div>
              <ArrowDown
                className="absolute bottom-4 right-4 bg-transparent cursor-pointer p-1 rounded text-[#2B1C25]"
                height={44}
                width={44}
              />
            </button>
          )}

          {/* See less Card */}
          {expandSites && filteredSites.length > 4 && (
            <button
              onClick={() => setExpandSites(false)}
              className="relative flex flex-col justify-between bg-[#AFAFAF] text-[#2B1C25] rounded-lg shadow hover:bg-[#9e8895] transition-all p-5 w-full"
            >
              <div className="flex flex-col items-start">
                <span className="text-2xl font-bold">See Less</span>
              </div>
              <ArrowUp
                className="absolute bottom-4 right-4 bg-transparent cursor-pointer p-1 rounded text-[#2B1C25]"
                height={44}
                width={44}
              />
            </button>
          )}
        </div>
      </div>

      <AddUserToProjectModal
        projectId={project?.id || 0}
        isOpen={isAddUserOpen}
        onClose={() => setIsAddUserOpen(false)}
      />
    </PageHeaderWrapper>
  );
};

export default ProjectView;
